import type { Delta } from '@amatiasq/state-delta';

import type { EntityId, PlayerInput, Team } from './entities.ts';

/** The path nginx forwards to the game server; the client asks for it. */
export const SOCKET_PATH = '/connect';

/**
 * A line of the scoreboard. It belongs to the socket that earned it, so it
 * outlives the player: dying and coming back is a new circle, not a new score.
 */
export interface Score {
  name: string;
  team: Team;
  kills: number;
}

/**
 * Something that happens once instead of lasting. Input is a state and the
 * server keeps the last word it heard; sending `reload: true` and then
 * `reload: false` would reload for ever the day the `false` went missing.
 */
export type PlayerAction = 'reload' | 'dash';

/**
 * An order carries `at`: the instant of this client's own clock it was given
 * at. The server anchors its answer to it, and that anchor is what tells a
 * prediction running ahead from one that is simply wrong — two positions 105 px
 * apart say nothing on their own, the same instant says everything.
 */
export type ClientMessage =
  | { type: 'join'; data: { name: string } }
  | { type: 'input'; data: Partial<PlayerInput>; at: number }
  | { type: 'action'; data: PlayerAction; at: number }
  | { type: 'ping'; data: number };

export type ServerMessage =
  | { type: 'welcome'; data: { playerId: EntityId; mapSize: number } }
  /**
   * A `state-delta` against what this client was last told, and how far into
   * that client's own clock this word reaches: the instant it stamped its last
   * order with, plus everything simulated since.
   */
  | { type: 'state'; data: Delta; applied: number }
  | { type: 'dead'; data: null }
  /** Sent when it changes — a kill, a join, a socket closing — and no oftener. */
  | { type: 'scores'; data: Score[] }
  | { type: 'pong'; data: number };
